import { c as _c } from "react/compiler-runtime";
import { isSeatLocked } from "@components/table/members/assign-seats/assign-seats-utils";
import { getInviteSeatOptions, MAX_INVITED_MEMBERS_WITH_SEATS } from "@components/table/members/assign-seats/invite-seats-utils";
import { useSeatsAvailable } from "@components/table/members/assign-seats/use-seats-available";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useMemberSeatsLookup } from "@hooks/use-member-seats-lookup";
import { doesWorkspaceRoleRequireXgSeat } from "@utils/xg-seat-utils";
import { useState } from "react";
/**
 * Returns the seats that can be given to the people invited to the workspace, and which of them are
 * checked.
 *
 * A role that needs a Sphere XG seat (admin, group manager, project manager) is never given without
 * one, so the seat is checked for those roles while the current user can give it. The SCENE seat is
 * never required.
 *
 * @param role The workspace role the people are invited with
 */
export function useInviteSeats(role) {
  const $ = _c(19);
  const {
    isActiveSeatPlan,
    hasSceneSeats
  } = useMemberSeatsLookup();
  const {
    canManageXgSeats,
    canManageSceneLicenses
  } = useHasUserValidRoleCompanyLevel();
  const seatsAvailable = useSeatsAvailable();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [selectedSeats, setSelectedSeats] = useState(t0);
  let isXgSeatRequired;
  let seatOptions;
  let t1;
  if ($[1] !== canManageSceneLicenses || $[2] !== canManageXgSeats || $[3] !== hasSceneSeats || $[4] !== isActiveSeatPlan || $[5] !== role || $[6] !== seatsAvailable || $[7] !== selectedSeats) {
    seatOptions = getInviteSeatOptions({
      isActiveSeatPlan,
      hasSceneSeats,
      canManageXgSeats,
      canManageSceneLicenses,
      seatsAvailable
    });
    isXgSeatRequired = doesWorkspaceRoleRequireXgSeat(role) && seatOptions.some(_temp);
    t1 = seatOptions.filter(option => !isSeatLocked(option) && (option.seat === "xgSeat" && isXgSeatRequired || !!selectedSeats[option.seat])).map(_temp2);
    $[1] = canManageSceneLicenses;
    $[2] = canManageXgSeats;
    $[3] = hasSceneSeats;
    $[4] = isActiveSeatPlan;
    $[5] = role;
    $[6] = seatsAvailable;
    $[7] = selectedSeats;
    $[8] = isXgSeatRequired;
    $[9] = seatOptions;
    $[10] = t1;
  } else {
    isXgSeatRequired = $[8];
    seatOptions = $[9];
    t1 = $[10];
  }
  const checkedSeats = t1;
  const t2 = checkedSeats.length ? MAX_INVITED_MEMBERS_WITH_SEATS : undefined;
  let t3;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (seat_1, isChecked) => setSelectedSeats(seats => ({
      ...seats,
      [seat_1]: isChecked
    }));
    t4 = () => setSelectedSeats({});
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  let t5;
  if ($[13] !== checkedSeats || $[14] !== isXgSeatRequired || $[15] !== seatOptions || $[16] !== seatsAvailable || $[17] !== t2) {
    t5 = {
      seatOptions,
      seatsAvailable,
      checkedSeats,
      isXgSeatRequired,
      maxInvitedMembers: t2,
      setSeatChecked: t3,
      resetSeats: t4
    };
    $[13] = checkedSeats;
    $[14] = isXgSeatRequired;
    $[15] = seatOptions;
    $[16] = seatsAvailable;
    $[17] = t2;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
function _temp2(t0) {
  const {
    seat: seat_0
  } = t0;
  return seat_0;
}
function _temp(t0) {
  const {
    seat
  } = t0;
  return seat === "xgSeat";
}